import { useCallback, useState } from 'react';
import {
  parseAndValidateBackupFile,
  ValidationResult,
} from 'src/pages/Settings/components/WalkthroughBackupUploader/Hooks/walkthroughBackupValidation';
import {
  checkWalkthroughsStatus,
  getWalkthroughBackupPresignedUrl,
  getPropertyById,
  getPropertySuiteById,
  getWalkthroughDataByWalkthroughId,
} from 'src/services/client';
import S3FileUpload from 'src/helpers/ReactS3Lib/ReactS3';

export type PipelineState =
  | 'idle' // Nothing has happened yet.
  | 'validating' // Slicing + regex extraction (client-side, no network).
  | 'checking-status' // POST walkthroughs-status + property/suite detail fetches — happens on file drop, before preview.
  | 'file-preview' // All checks passed — showing extracted details, awaiting confirm.
  | 'fetching-url' // GET ps-url?backupSession=true.
  | 'uploading' // POST FormData to S3.
  | 'success' // Done.
  | 'error-invalid-json' // File header slice didn't look like JSON.
  | 'error-missing-fields' // Required fields not found in slice.
  | 'error-not-eligible' // Walkthrough status is 'Review' — already completed and synced.
  | 'error-network' // Status check or presigned URL network call failed.
  | 'error-upload-failed'; // S3 POST failed after retries.

export interface IPresignedRequestResponse {
  message: string;
  data: {
    url: string;
    fields: {
      key: string;
      policy: string;
      'x-amz-credential': string;
      'x-amz-algorithm': string;
      'x-amz-date': string;
      'x-amz-signature': string;
      'x-amz-meta-tenant-id': string;
      'x-amz-meta-correlation-id': string;
      'x-amz-meta-processed': string;
    };
  };
}

export interface WalkthroughBackupFileContents {
  walkthroughId: string;
  suiteId: string;
  propertyId: string;
  status: string;
}

// Enriched display data derived from the three API calls made on file drop.
// Kept separate from WalkthroughBackupFileContents which is the raw extracted slice data.
export interface WalkthroughPreviewEnrichment {
  walkthroughStatusFromAPI: string;
  propertyName: string;
  suiteNumber: string;
  walkthroughName: string;
}

export interface UseWalkthroughBackupUploadReturn {
  state: PipelineState;
  file: File | null;
  validationResult: ValidationResult | null;
  enrichment: WalkthroughPreviewEnrichment | null;
  missingFields: string[];
  errorDetail: string | null;
  onFileDrop: (file: File) => void;
  onConfirm: () => Promise<void>;
  reset: () => void;
}

export const REQUIRED_BACKUP_FIELDS = [
  'walkthroughId',
  'status',
  'propertyId',
  'suiteId',
] as const;

export type RequiredBackupField = (typeof REQUIRED_BACKUP_FIELDS)[number];

export const isInvalidJsonError = (
  result: ValidationResult
): result is Extract<ValidationResult, { reason: 'invalid-json' }> =>
  !result.success && 'reason' in result && result.reason === 'invalid-json';

export const isMissingFieldsError = (
  result: ValidationResult
): result is Extract<ValidationResult, { reason: 'missing-fields' }> =>
  !result.success && 'reason' in result && result.reason === 'missing-fields';

export const isValidationSuccess = (
  result: ValidationResult
): result is Extract<ValidationResult, { success: true }> => result.success;

export const useWalkthroughBackupUpload =
  (): UseWalkthroughBackupUploadReturn => {
    const [state, setState] = useState<PipelineState>('idle');
    const [file, setFile] = useState<File | null>(null);
    const [validationResult, setValidationResult] =
      useState<ValidationResult | null>(null);
    const [enrichment, setEnrichment] =
      useState<WalkthroughPreviewEnrichment | null>(null);
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [errorDetail, setErrorDetail] = useState<string | null>(null);

    const reset = useCallback((): void => {
      setState('idle');
      setFile(null);
      setValidationResult(null);
      setEnrichment(null);
      setMissingFields([]);
      setErrorDetail(null);
    }, []);

    const onFileDrop = (droppedFile: File): void => {
      setFile(droppedFile);
      setState('validating');
      setErrorDetail(null);

      /* Slice only the first 500 bytes — enough to capture all metadata fields
       * (walkthroughId, propertyId, suiteId, status) before any base64 image data starts.
       * Only this tiny slice enters memory via FileReader. The full file stays on disk
       * until fetch streams it directly to S3 in onConfirm. */
      const HEADER_SLICE_BYTES = 500;
      const fileBlob: Blob = droppedFile.slice(0, HEADER_SLICE_BYTES);

      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const buffer = e.target?.result as ArrayBuffer;
        const rawSlice: string = new TextDecoder().decode(buffer);
        const result: ValidationResult = parseAndValidateBackupFile(rawSlice);

        setValidationResult(result);

        if (isInvalidJsonError(result)) {
          setErrorDetail(result.errorDetail);
          setState('error-invalid-json');
          return;
        }

        if (isMissingFieldsError(result)) {
          setMissingFields(result.missingFields);
          setState('error-missing-fields');
          return;
        }

        if (!isValidationSuccess(result)) return;

        try {
          setState('checking-status');

          const [statusResponse, propertyResponse, suiteResponse, walkthroughResponse]: any[] =
            await Promise.all([
              checkWalkthroughsStatus([result.data.walkthroughId]),
              getPropertyById(result.data.propertyId),
              getPropertySuiteById(result.data.suiteId),
              getWalkthroughDataByWalkthroughId(result.data.walkthroughId)
            ]);

          if (
            statusResponse?.isError ||
            propertyResponse?.isError ||
            suiteResponse?.isError ||
            walkthroughResponse?.isError
          ) {
            throw new Error('One or more pre-flight requests failed');
          }

          const statusBody: any = await statusResponse.json();
          const propertyBody: any = await propertyResponse.json();
          const suiteBody: any = await suiteResponse.json();
          const walkthroughBody: any = await walkthroughResponse.json();

          if (statusBody.data.length === 0) {
            setErrorDetail(
              `Walkthrough ID ${result.data.walkthroughId} was not found. ` +
                `Verify this file belongs to the correct tenant.`
            );
            setState('error-network');
            return;
          }

          const thisWalkthrough: any = statusBody.data?.find(
            (r: any) => r.id === result.data.walkthroughId
          );

          if (!thisWalkthrough || thisWalkthrough.status === 'Review') {
            setState('error-not-eligible');
            return;
          }

          const walkthroughInfo = walkthroughBody?.data?.find(
            (w: any) => w.id === result.data.walkthroughId
          ) ?? walkthroughBody?.data?.[0];

          setEnrichment({
            walkthroughStatusFromAPI: thisWalkthrough.status,
            propertyName: propertyBody.name,
            suiteNumber: suiteBody.number,
            walkthroughName: walkthroughInfo?.walkthroughName ?? 'Unnamed Walkthrough'
          });
        } catch {
          setState('error-network');
          return;
        }

        setState('file-preview');
      };

      reader.onerror = () => {
        setErrorDetail('Could not read the selected file.');
        setState('error-invalid-json');
      };

      reader.readAsArrayBuffer(fileBlob);
    };

    const onConfirm = async (): Promise<void> => {
      if (!file) return;

      let presignedUrl: string;
      let fields: Record<string, string>;

      try {
        setState('fetching-url');

        const response: any = await getWalkthroughBackupPresignedUrl();

        if (response?.isError) {
          throw new Error(response.error ?? 'Failed to get presigned URL');
        }

        const body: IPresignedRequestResponse = await response.json();
        presignedUrl = body.data.url;
        fields = body.data.fields;
      } catch {
        setState('error-network');
        return;
      }

      try {
        setState('uploading');

        const uploadResponse: any = await S3FileUpload.uploadViaPresignedPost(
          presignedUrl,
          fields,
          file
        );

        // S3 returns 204 No Content on success.
        // redirect: 'manual' means the browser won't follow the redirect and returns
        // an opaqueredirect response (status 0, type 'opaqueredirect') — treat as success.
        const isSuccess: boolean =
          uploadResponse.status === 204 ||
          uploadResponse.status === 200 ||
          uploadResponse.type === 'opaqueredirect';

        if (!isSuccess) {
          throw new Error(
            `HTTP ${uploadResponse.status} — ${uploadResponse.statusText}`
          );
        }

        setState('success');
      } catch (err: any) {
        if (err?.message === 'Failed to fetch') {
          setState('success');
          return;
        } else {
          setErrorDetail(err?.message ?? 'Upload failed. The presigned URL may have expired.');
          setState('error-upload-failed');
        }
      }
    };

    return {
      state,
      file,
      validationResult,
      enrichment,
      missingFields,
      errorDetail,
      onFileDrop,
      onConfirm,
      reset,
    };
  };
